import { GcpBox, GcpPasswordInput, GcpTypography } from '@back-office/ui';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextFields from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BUTTON_CLICK_WAIT } from '../../../constants';
import { LoginFieldNames, LoginStep } from '../../../forms';
import useViewModel from './viewmodel';

type TimerProps = {
  forgotPasswordButtonWaitTime: number;
};

const Timer: FC<TimerProps> = observer(({ forgotPasswordButtonWaitTime }) => {
  const useCustomStyles = makeStyles(
    () => ({
      timer_text: {
        bottom: '30px',
        fontSize: '11px',
        position: 'relative',
      },
      timer_icon: { color: '#858b9a' },
    }),
    { name: 'CustomStyle' }
  );

  const classes = useCustomStyles();

  return (
    <GcpBox hidden={forgotPasswordButtonWaitTime <= 0}>
      <CircularProgress
        className={classes.timer_icon}
        size={30}
        value={
          ((BUTTON_CLICK_WAIT - forgotPasswordButtonWaitTime) * 100) /
          BUTTON_CLICK_WAIT
        }
        variant="determinate"
      />
      <Typography className={classes.timer_text}>
        {forgotPasswordButtonWaitTime}s
      </Typography>
    </GcpBox>
  );
});

const LoginComponent: FC = observer(() => {
  const { t } = useTranslation();
  const {
    control,
    formState,
    handleOnSubmit,
    handleSubmit,
    forgotPasswordButtonWaitTime,
    setLoginStep,
    state,
  } = useViewModel();

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleOnSubmit)}>
      <GcpBox isFullWidth mt={2}>
        <Controller
          control={control}
          defaultValue=""
          name={LoginFieldNames.Email}
          render={({ onChange, name, value, onBlur }) => (
            <TextFields
              fullWidth
              label={t('login_form.email')}
              name={name}
              type="text"
              value={value}
              variant="outlined"
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </GcpBox>
      <GcpBox isFullWidth mt={2}>
        <Controller
          control={control}
          defaultValue=""
          name={LoginFieldNames.Password}
          render={({ onChange, name, value, onBlur }) => (
            <GcpPasswordInput
              label={t('login_form.password')}
              name={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </GcpBox>
      {state.errorMsg && (
        <GcpBox mt={2}>
          <Alert severity="error">
            <GcpTypography paint="flamingo" variant="body1">
              {t(state.errorMsg)}
            </GcpTypography>
          </Alert>
        </GcpBox>
      )}
      <GcpBox isFullWidth mt={2}>
        <Button
          fullWidth
          color="primary"
          disabled={!formState.isValid}
          size="large"
          type="submit"
          variant="contained"
        >
          {t('login_form.sign_in')}
        </Button>
      </GcpBox>
      <GcpBox
        isFullWidth
        display="flex"
        justifyContent="center"
        mt={2}
        textAlign="center"
      >
        <GcpBox>
          <Button
            disabled={forgotPasswordButtonWaitTime > 0}
            variant="text"
            onClick={() => setLoginStep(LoginStep.ForgotPassword)}
          >
            <GcpTypography paint="hitGray" variant="body1">
              {t('login_form.forgot_password')}
            </GcpTypography>
          </Button>
        </GcpBox>
        <GcpBox>
          <Timer forgotPasswordButtonWaitTime={forgotPasswordButtonWaitTime} />
        </GcpBox>
      </GcpBox>
    </form>
  );
});

export default LoginComponent;
